import Auth, { Environment, AuthRequestMethods } from '@hotmart/oauth-ui'
import Request from '@hotmart-org-ca/request'

import { GetMarketplaceProfileResponse, LocalStorageKey } from './types'

import ApiHelper from 'common/helpers/api'
import { User, Authority } from '../types'
import './request'

class Service extends Auth implements AuthRequestMethods {
  isProduction = process.env.NODE_ENV === 'production'

  environment: Environment = this.isProduction ? 'prod' : 'dev'

  refs = {
    integrationToken: 'hmClubIntegration',
    auth: '__amplify__oauthData',
    token: 'token',
    user: '__amplify__accountData',
    checkToken: '__amplify__checkTokenData',
    productToken: 'productToken',
    environment: this.environment
  }

  loginRequest = (opt: Record<string, unknown>) => Request('auth.login', opt)

  logoutRequest = (opt: Record<string, unknown>) => Request('auth.logout', opt)

  checkRequest = (opt: Record<string, unknown>) => Request('auth.check_token', opt)

  private getIfIsLocal = () => {
    const location = window.location.href

    return Boolean(location.includes('localhost'))
  }

  get isLogged() {
    return Boolean(this.getToken())
  }

  get isLocal() {
    return this.getIfIsLocal()
  }

  setToken = (token: string) => {
    localStorage.setItem(LocalStorageKey.TOKEN, token)
  }

  getToken = () => {
    return localStorage.getItem(LocalStorageKey.TOKEN)
  }

  updateHeaders = () => {
    localStorage.setItem(LocalStorageKey.TOKEN, this.token)
    Request.setDefaultHeaders({ token: this.token })
  }

  loginUser = async (username: string, password: string) => {
    await this.login(username, password)
    this.updateHeaders()
  }

  getUserByToken = async (accessToken: string) => {
    const user = await Request('auth.token.check', { 'access_token': accessToken })

    return user as User
  }

  getProfile = (accessToken: string) => {
    try {
      return Request('auth.user_profile', { token: accessToken }) as Promise<GetMarketplaceProfileResponse>
    } catch (error) {
      throw ApiHelper.errorHandling(error)
    }
  }

  getAuthorities = (accessToken: string) => {
    try {
      return Request('auth.check_token', { token: accessToken })
    } catch (error) {
      return ApiHelper.errorHandling(error)
    }
  }

  clear = () => {
    localStorage.removeItem(LocalStorageKey.TOKEN)
    localStorage.removeItem(LocalStorageKey.USER)
  }

  getUser = (): User | undefined => {
    const userData = localStorage.getItem(LocalStorageKey.USER)

    return userData ? JSON.parse(userData).user as User : void 0
  }

  setUser = (user: User) => {
    localStorage.setItem(LocalStorageKey.USER, JSON.stringify({ user }))
  }

  isTokenEncoded = (token: string) => {
    return decodeURIComponent(token) !== token
  }

  encodeToken = (token: string) => {
    return this.isTokenEncoded(token) ? token : encodeURIComponent(token)
  }

  hasAuthority = (authority: Authority) => {
    const user = this.getUser()

    return user?.authorities.includes(authority) || false
  }

  checkYoutubeIntegration = async () => {
    try {
      await Request('oauth.validate_token')
      return true
    } catch {
      return false
    }
  }

  sendYoutubeIntegration = async (code: string, redirectUri?: string) => {
    const check = await this.checkYoutubeIntegration()

    if (!check) {
      await Request('oauth.refresh_token', { code, redirectUri })
    }
  }

  getUserLocale = (): string | undefined => {
    const user = this.getUser()

    if (this.isLogged && user) {
      return user.locale
    }

    return void 0
  }
}

export default new Service()
